/**
 * Time Column
 *
 * A single day column in the time grid, showing background slots
 * and absolutely positioned EventBlocks.
 */

import { useState, useCallback } from 'react';
import { isToday, startOfDay } from 'date-fns';
import clsx from 'clsx';
import { SLOT_HEIGHT_PX, SLOT_DURATION_MS, DEFAULT_START_HOUR, DEFAULT_END_HOUR } from '@/utils/scheduleLayout';
import { DEFAULT_DURATION_MS } from '@/hooks/useScheduler';
import { EventBlock } from './EventBlock';
import { CurrentTimeLine } from './CurrentTimeLine';
import type { PositionedItem } from '@/hooks/useScheduler';

interface TimeColumnProps {
  day: Date;
  items: PositionedItem[];
  startHour?: number;
  endHour?: number;
  dragItemId?: string | null;
  ghostTop?: number | null;
  ghostHeight?: number | null;
  onItemPointerDown?: (
    e: React.PointerEvent,
    itemId: string,
    startTime: number,
    endTime: number,
    dayDate: Date
  ) => void;
  onResizePointerDown?: (
    e: React.PointerEvent,
    itemId: string,
    startTime: number,
    endTime: number,
    dayDate: Date,
    edge: 'top' | 'bottom'
  ) => void;
  onSlotPointerDown?: (e: React.PointerEvent, dayDate: Date) => void;
  onItemClick?: (item: PositionedItem) => void;
  onDropUnscheduledTask?: (taskId: string, start: number, end: number) => void;
}

export function TimeColumn({
  day,
  items,
  startHour = DEFAULT_START_HOUR,
  endHour = DEFAULT_END_HOUR,
  dragItemId,
  ghostTop,
  ghostHeight,
  onItemPointerDown,
  onResizePointerDown,
  onSlotPointerDown,
  onItemClick,
  onDropUnscheduledTask,
}: TimeColumnProps) {
  const totalSlots = (endHour - startHour) * 2; // 30-min slots
  const totalHeight = totalSlots * SLOT_HEIGHT_PX;
  const today = isToday(day);

  // Drop zone state for unscheduled task drag
  const [dropGhostTop, setDropGhostTop] = useState<number | null>(null);

  const snapInterval = 15 * 60 * 1000; // 15 minutes

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('application/zenda-task')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    // Snap to 15-min slots (SLOT_HEIGHT_PX / 2 = 15 min)
    const slotPx = SLOT_HEIGHT_PX / 2;
    const snappedY = Math.round(y / slotPx) * slotPx;
    setDropGhostTop(Math.max(0, Math.min(snappedY, totalHeight - SLOT_HEIGHT_PX)));
  }, [totalHeight]);

  const handleDragLeave = useCallback(() => {
    setDropGhostTop(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    const taskId = e.dataTransfer.getData('application/zenda-task');
    if (!taskId || !onDropUnscheduledTask) {
      setDropGhostTop(null);
      return;
    }
    e.preventDefault();

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const slotPx = SLOT_HEIGHT_PX / 2;
    const snappedY = Math.round(y / slotPx) * slotPx;

    // Convert Y to timestamp
    const dayStartMs = startOfDay(day).getTime() + startHour * 60 * 60 * 1000;
    const msPerPx = SLOT_DURATION_MS / SLOT_HEIGHT_PX;
    const startMs = dayStartMs + snappedY * msPerPx;

    // Snap to 15 minutes
    const snappedStart = Math.round(startMs / snapInterval) * snapInterval;
    const snappedEnd = snappedStart + DEFAULT_DURATION_MS;

    onDropUnscheduledTask(taskId, snappedStart, snappedEnd);
    setDropGhostTop(null);
  }, [day, startHour, snapInterval, onDropUnscheduledTask]);

  return (
    <div
      className={clsx(
        'flex-1 border-r border-border-primary last:border-r-0 relative',
        today && 'bg-accent-violet/[0.02]'
      )}
      style={{ height: `${totalHeight}px` }}
      data-time-grid-column
      onPointerDown={(e) => {
        // Only trigger on direct click on the column background, not on events
        if ((e.target as HTMLElement).closest('[data-event-block]')) return;
        onSlotPointerDown?.(e, day);
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Background slot lines */}
      {Array.from({ length: totalSlots }, (_, i) => (
        <div
          key={i}
          className={clsx(
            'border-b',
            i % 2 === 0
              ? 'border-border-primary/60'
              : 'border-border-primary/30'
          )}
          style={{ height: `${SLOT_HEIGHT_PX}px` }}
        />
      ))}

      {/* Current time line */}
      <CurrentTimeLine dayDate={day} startHour={startHour} />

      {/* Event blocks */}
      {items.map((item) => (
        <div key={item.id} data-event-block>
          <EventBlock
            item={item}
            isDragging={dragItemId === item.id}
            onClick={() => onItemClick?.(item)}
            onPointerDown={(e) =>
              onItemPointerDown?.(e, item.id, item.startTime, item.endTime, day)
            }
            onResizePointerDown={(e, edge) =>
              onResizePointerDown?.(e, item.id, item.startTime, item.endTime, day, edge)
            }
          />
        </div>
      ))}

      {/* Drag ghost (pointer DnD) */}
      {dragItemId && ghostTop != null && ghostHeight != null && (
        <div
          className="absolute left-1 right-1 rounded-md bg-accent-violet/15 border-2 border-dashed border-accent-violet/30 pointer-events-none z-20"
          style={{
            top: `${ghostTop}px`,
            height: `${ghostHeight}px`,
          }}
        />
      )}

      {/* Drop ghost (HTML5 DnD from unscheduled panel) */}
      {dropGhostTop != null && (
        <div
          className="absolute left-1 right-1 rounded-md bg-accent-emerald/15 border-2 border-dashed border-accent-emerald/40 pointer-events-none z-20"
          style={{
            top: `${dropGhostTop}px`,
            height: `${SLOT_HEIGHT_PX * 2}px`, // 1 hour default
          }}
        />
      )}
    </div>
  );
}
